import {
  ClaimRequestsAction,
  ClaimRequestsActionTypes
} from './claim-requests.actions';
import { ClaimRequest } from '@ecosure-suite/shared/util-entities';
import { createEntityAdapter, EntityState } from '@ngrx/entity';
import { Pagination } from '@ecosure-suite/shared/utils-helpers';

export const CLAIMREQUESTS_FEATURE_KEY = 'clClaimRequests';

/**
 * Interface for the 'ClaimRequests' data used in
 *  - ClaimRequestsState, and
 *  - claimRequestsReducer
 *
 *  Note: replace if already defined in another module
 */
export const claimRequestEntityAdapter = createEntityAdapter<ClaimRequest>({
  selectId: (claimRequest: ClaimRequest) => claimRequest.id,
  sortComparer: false
});

export interface ClaimRequestsState extends EntityState<ClaimRequest> {
  selectedId?: number; // which ClaimRequests record has been selected
  searchByIdNumberIds: number[];
  searchByIdNumberPagination: Pagination;
  listByIdNumberIds: number[];
  listByIdNumberPagination: Pagination;
  listByStatusIds: number[];
  listByStatusPagination: Pagination;
  listForSupervisorByStatusIds: number[];
  listForSupervisorByStatusPagination: Pagination;
  listByAssigneeIds: number[];
  listByAssigneePagination: Pagination;
  listByCurrentUserIds: number[];
  listByCurrentUserPagination: Pagination;
  loaded: boolean; // has the ClaimRequests list been loaded
  error?: any; // last none error (if any)
}

export interface ClaimRequestsPartialState {
  readonly [CLAIMREQUESTS_FEATURE_KEY]: ClaimRequestsState;
}

export const initialState: ClaimRequestsState = claimRequestEntityAdapter.getInitialState(
  {
    searchByIdNumberIds: [],
    searchByIdNumberPagination: {
      currentPage: 1,
      totalItems: 0,
      itemsPerPage: 25,
      totalPages: 0
    },
    listByIdNumberIds: [],
    listByIdNumberPagination: {
      currentPage: 1,
      totalItems: 0,
      itemsPerPage: 25,
      totalPages: 0
    },
    listByStatusIds: [],
    listByStatusPagination: {
      currentPage: 1,
      totalItems: 0,
      itemsPerPage: 25,
      totalPages: 0
    },
    listForSupervisorByStatusIds: [],
    listForSupervisorByStatusPagination: {
      currentPage: 1,
      totalItems: 0,
      itemsPerPage: 25,
      totalPages: 0
    },
    listByAssigneeIds: [],
    listByAssigneePagination: {
      currentPage: 1,
      totalItems: 0,
      itemsPerPage: 25,
      totalPages: 0
    },
    listByCurrentUserIds: [],
    listByCurrentUserPagination: {
      currentPage: 1,
      totalItems: 0,
      itemsPerPage: 25,
      totalPages: 0
    },
    loaded: true
  }
);

export function claimRequestsReducer(
  state: ClaimRequestsState = initialState,
  action: ClaimRequestsAction
): ClaimRequestsState {
  switch (action.type) {
    case ClaimRequestsActionTypes.GET_CLAIM_REQUEST:
    case ClaimRequestsActionTypes.CREATE_CLAIM_REQUEST:
    case ClaimRequestsActionTypes.EDIT_CLAIM_REQUEST:
    case ClaimRequestsActionTypes.LIST_CLAIM_REQUESTS_BY_ID_NUMBER:
    case ClaimRequestsActionTypes.SEARCH_CLAIM_REQUESTS_BY_ID_NUMBER:
    case ClaimRequestsActionTypes.LIST_CLAIM_REQUESTS_BY_STATUS:
    case ClaimRequestsActionTypes.LIST_SUPERVISOR_CLAIM_REQUESTS_BY_STATUS:
    case ClaimRequestsActionTypes.LIST_CLAIM_REQUESTS_BY_ASSIGNEE:
    case ClaimRequestsActionTypes.LIST_CLAIM_REQUESTS_BY_CURRENT_USER:
    case ClaimRequestsActionTypes.ASSIGN_CLAIM_REQUEST_TO_VERIFIER:
      return {
        ...state,
        loaded: false,
        error: undefined
      };

    case ClaimRequestsActionTypes.GET_CLAIM_REQUEST_SUCCESS:
      return {
        ...claimRequestEntityAdapter.addOne(action.payload, state),
        selectedId: action.payload.id,
        loaded: true
      };

    case ClaimRequestsActionTypes.CREATE_CLAIM_REQUEST_SUCCESS:
      return {
        ...claimRequestEntityAdapter.addOne(action.payload.claimRequest, state),
        selectedId: action.payload.claimRequest.id,
        loaded: true
      };

    case ClaimRequestsActionTypes.LIST_CLAIM_REQUESTS_BY_ID_NUMBER_SUCCESS:
      return {
        ...claimRequestEntityAdapter.addMany(
          action.payload.claimRequests,
          state
        ),
        listByIdNumberIds: action.payload.claimRequests.map(
          claimRequest => claimRequest.id
        ),
        listByIdNumberPagination: Object.assign(
          {},
          state.listByIdNumberPagination,
          action.payload.page
        ),
        loaded: true
      };

    case ClaimRequestsActionTypes.SEARCH_CLAIM_REQUESTS_BY_ID_NUMBER_SUCCESS:
      return {
        ...claimRequestEntityAdapter.addMany(
          action.payload.claimRequests,
          state
        ),
        searchByIdNumberIds: action.payload.claimRequests.map(
          claimRequest => claimRequest.id
        ),
        searchByIdNumberPagination: Object.assign(
          {},
          state.searchByIdNumberPagination,
          action.payload.page
        ),
        loaded: true
      };

    case ClaimRequestsActionTypes.LIST_CLAIM_REQUESTS_BY_STATUS_SUCCESS:
      return {
        ...claimRequestEntityAdapter.addMany(
          action.payload.claimRequests,
          state
        ),
        listByStatusIds: action.payload.claimRequests.map(
          claimRequest => claimRequest.id
        ),
        listByStatusPagination: Object.assign(
          {},
          state.listByStatusPagination,
          action.payload.page
        ),
        loaded: true
      };

    case ClaimRequestsActionTypes.LIST_SUPERVISOR_CLAIM_REQUESTS_BY_STATUS_SUCCESS:
      return {
        ...claimRequestEntityAdapter.addMany(
          action.payload.claimRequests,
          state
        ),
        listForSupervisorByStatusIds: action.payload.claimRequests.map(
          claimRequest => claimRequest.id
        ),
        listForSupervisorByStatusPagination: Object.assign(
          {},
          state.listForSupervisorByStatusPagination,
          action.payload.page
        ),
        loaded: true
      };

    case ClaimRequestsActionTypes.LIST_CLAIM_REQUESTS_BY_ASSIGNEE_SUCCESS:
      return {
        ...claimRequestEntityAdapter.addMany(
          action.payload.claimRequests,
          state
        ),
        listByAssigneeIds: action.payload.claimRequests.map(
          claimRequest => claimRequest.id
        ),
        listByAssigneePagination: Object.assign(
          {},
          state.listByAssigneePagination,
          action.payload.page
        ),
        loaded: true
      };

    case ClaimRequestsActionTypes.LIST_CLAIM_REQUESTS_BY_CURRENT_USER_SUCCESS:
      return {
        ...claimRequestEntityAdapter.addMany(
          action.payload.claimRequests,
          state
        ),
        listByCurrentUserIds: action.payload.claimRequests.map(
          claimRequest => claimRequest.id
        ),
        listByCurrentUserPagination: Object.assign(
          {},
          state.listByCurrentUserPagination,
          action.payload.page
        ),
        loaded: true
      };

    case ClaimRequestsActionTypes.EDIT_CLAIM_REQUEST_SUCCESS:
    case ClaimRequestsActionTypes.ASSIGN_CLAIM_REQUEST_TO_VERIFIER_SUCCESS:
      return {
        ...claimRequestEntityAdapter.updateOne(
          {
            id: action.payload.id,
            changes: action.payload
          },
          state
        ),
        loaded: true
      };

    case ClaimRequestsActionTypes.SELECT_REQUEST_REQUEST:
      return {
        ...state,
        selectedId: action.payload
      };

    case ClaimRequestsActionTypes.CLEAR_REQUEST_REQUEST:
      return {
        ...state,
        selectedId: undefined
      };

    case ClaimRequestsActionTypes.GET_CLAIM_REQUEST_FAIL:
    case ClaimRequestsActionTypes.CREATE_CLAIM_REQUEST_FAIL:
    case ClaimRequestsActionTypes.EDIT_CLAIM_REQUEST_FAIL:
    case ClaimRequestsActionTypes.SEARCH_CLAIM_REQUESTS_BY_ID_NUMBER_FAIL:
    case ClaimRequestsActionTypes.LIST_CLAIM_REQUESTS_BY_ID_NUMBER_FAIL:
    case ClaimRequestsActionTypes.LIST_CLAIM_REQUESTS_BY_STATUS_FAIL:
    case ClaimRequestsActionTypes.LIST_SUPERVISOR_CLAIM_REQUESTS_BY_STATUS_FAIL:
    case ClaimRequestsActionTypes.LIST_CLAIM_REQUESTS_BY_ASSIGNEE_FAIL:
    case ClaimRequestsActionTypes.LIST_CLAIM_REQUESTS_BY_CURRENT_USER_FAIL:
    case ClaimRequestsActionTypes.ASSIGN_CLAIM_REQUEST_TO_VERIFIER_FAIL:
      return {
        ...state,
        loaded: true,
        error: action.payload
      };

    default:
      return state;

    // case claimLine.LIST_CLAIM_LINE_BY_STATUS_SUCCESS:
    // case claimLine.LIST_CLAIM_LINE_BY_ASSIGNEE_SUCCESS:
    //   return {
    //     ...claimRequestEntityAdapter.addMany(
    //       action.payload.claimLines.map(line => line.claimRequest),
    //       state
    //     )
    //   };
  }
}
